import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { User, Heart, FileText, Trash2, ChevronDown, ChevronUp } from "lucide-react";

export function AccountLayout() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (!loading && !user) nav("/login"); }, [loading, user, nav]);
  if (!user) return null;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-12 gap-8" data-testid="account-layout">
      <aside className="lg:col-span-3">
        <div className="bg-white border border-gray-100 rounded-xl p-5">
          <div className="font-display font-semibold">Hi, {user.name.split(" ")[0]}</div>
          <div className="text-xs text-muted-foreground">{user.email}</div>
          <nav className="mt-5 space-y-1 text-sm">
            <SideLink to="/account" end icon={User} label="Profile" testid="acct-profile" />
            <SideLink to="/account/wishlist" icon={Heart} label="Wishlist" testid="acct-wishlist" />
            <SideLink to="/account/inquiries" icon={FileText} label="My Inquiries" testid="acct-inquiries" />
          </nav>
        </div>
      </aside>
      <div className="lg:col-span-9"><Outlet /></div>
    </main>
  );
}

const SideLink = ({ to, icon: Icon, label, end, testid }) => (
  <NavLink end={end} to={to} data-testid={testid}
    className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-md ${isActive ? "bg-primary/10 text-primary font-semibold" : "text-foreground/70 hover:bg-muted/60"}`}>
    <Icon className="w-4 h-4" /> {label}
  </NavLink>
);

export function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", organization: "", address: "" , pincode: "", gst: ""});
  useEffect(() => { if (user) setForm({ name: user.name, phone: user.phone || "", organization: user.organization || "", address: user.address || "" , pincode: user.pincode || "", gst: user.gst || ""}); }, [user]);
  const save = async (e) => { e.preventDefault(); await updateProfile(form); toast.success("Profile updated"); };
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-6" data-testid="profile-page">
      <h1 className="font-display text-2xl font-bold mb-5">Profile</h1>
      <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 max-w-2xl">
        <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" /></Field>
        <Field label="Email"><Input value={user?.email || ""} disabled /></Field>
        <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" /></Field>
        <Field label="Organization"><Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="profile-org" /></Field>
        <Field label="PINCODE"><Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} data-testid="pincode-org" /></Field>
        <Field label="GSTIN"><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} data-testid="gst-org" /></Field>
        <div className="sm:col-span-2">
          <Field label="Address"><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="profile-address" /></Field>
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="profile-save">Save changes</Button>
        </div>
      </form>
    </div>
  );
}

const DEFAULT_PRODUCT_IMAGE = "https://nivilabs.bionivid.com/resources/images/assest.static.default.product.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export function WishlistPage() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/wishlist").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { await api.delete(`/wishlist/${id}`); load(); toast.success("Removed"); };
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-6" data-testid="wishlist-page">
      <h1 className="font-display text-2xl font-bold mb-5">Wishlist</h1>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground">No saved products yet.</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((p) => (
            <div key={p.id} className="flex gap-3 p-3 border border-gray-100 rounded-lg" data-testid={`wish-${p.id}`}>
              <img src={p.images?.[0] ? `${BACKEND_URL}${p.images?.[0]}` : DEFAULT_PRODUCT_IMAGE} alt="" className="w-20 h-20 rounded object-cover" />
              <div className="flex-1">
                <Link to={`/product/${p.id}`} className="font-medium text-sm hover:text-primary">{p.name}</Link>
                <div className="text-xs text-muted-foreground">{p.description}</div>
                <button onClick={() => remove(p.id)} className="mt-2 text-xs text-destructive flex items-center gap-1"><Trash2 className="w-3 h-3" />Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_COLOR = {
  new: "bg-secondary/10 text-secondary",
  contacted: "bg-accent/30 text-accent-foreground",
  quote_sent: "bg-primary/10 text-primary",
  negotiating: "bg-accent/30 text-accent-foreground",
  converted: "bg-primary/15 text-primary",
  closed: "bg-muted text-muted-foreground",
};

export function InquiriesPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("latest");
  const [expandedId, setExpandedId] = useState(null);
  useEffect(() => { api.get("/inquiries/mine").then((r) => setItems(r.data)); }, []);

  const statuses = ["latest", "all", "new", "contacted", "quote_sent", "negotiating", "converted", "closed"];
  const formatDate = (value) => value ? new Date(value).toLocaleString() : "—";

  const filteredItems = items
    .slice()
    .filter((inq) => filter === "all" || filter === "latest" || inq.status === filter)
    .sort((a, b) => {
      if (filter === "latest") {
        return new Date(b.updated_at) - new Date(a.updated_at);
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-6" data-testid="my-inquiries-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl font-bold">My Inquiries</h1>
          <p className="text-sm text-muted-foreground mt-1">Review your inquiry history and view latest status updates.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {statuses.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setFilter(status)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${filter === status ? "bg-primary text-white" : "bg-muted/60 text-foreground hover:bg-muted"}`}
              data-testid={`inquiry-filter-${status}`}
            >
              {status === "latest" ? "Latest" : status === "all" ? "All" : status.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground">You haven't submitted any inquiries yet.</div>
      ) : (
        <>
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="p-3 text-left">Inquiry ID</th>
                  <th className="p-3 text-left">Submitted</th>
                  <th className="p-3 text-left">Last update</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Products</th>
                  <th className="p-3 text-left">Notes</th>
                  <th className="p-3 text-left">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredItems.map((inq) => (
                  <React.Fragment key={inq.id}>
                    <tr
                      className="bg-white hover:bg-muted/20 cursor-pointer transition-all"
                      onClick={() => setExpandedId(expandedId === inq.id ? null : inq.id)}
                      data-testid={`my-inq-${inq.id}`}
                    >
                      <td className="p-3 align-top font-mono text-xs text-muted-foreground break-all max-w-[180px]">{inq.id}</td>
                      <td className="p-3 align-top text-xs text-muted-foreground">{formatDate(inq.created_at)}</td>
                      <td className="p-3 align-top text-xs text-muted-foreground">{formatDate(inq.updated_at)}</td>
                      <td className="p-3 align-top">
                        <Badge className={`${STATUS_COLOR[inq.status] || "bg-muted text-muted-foreground"} border-0`}>
                          {String(inq.status || "unknown").replace(/_/g, " ")}
                        </Badge>
                      </td>
                      <td className="p-3 align-top">{inq.items?.length ?? 0}</td>
                      <td className="p-3 align-top max-w-[240px] text-sm text-muted-foreground truncate">{inq.notes || "—"}</td>
                      <td className="p-3 align-top">
                        {expandedId === inq.id ? <ChevronUp className="w-4 h-4 text-primary" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                      </td>
                    </tr>
                    {expandedId === inq.id && (
                      <tr className="bg-slate-50">
                        <td colSpan={7} className="p-4 border-t border-gray-100">
                          <div className="space-y-4">
                            {Array.isArray(inq.items) && inq.items.length > 0 ? (
                              inq.items.map((item, index) => (
                                <div key={`${item.product_id}-${index}`} className="rounded-xl border border-gray-200 bg-white p-4">
                                  <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                      <div className="text-sm font-medium">{item.name || item.catalogNumber || "Product"}</div>
                                      <div className="text-xs text-muted-foreground mt-1">Catalog No: {item.catalogNumber || "—"}</div>
                                    </div>
                                    <div className="text-sm font-semibold">Qty: {item.quantity ?? item.qty ?? 1}</div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-sm text-muted-foreground">No items available for this inquiry.</div>
                            )}

                            {Array.isArray(inq.admin_notes && inq.admin_notes.length ? inq.admin_notes : inq.adminNotes) && (
                              <div className="rounded-xl border border-gray-200 bg-white p-4">
                                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Admin notes</div>
                                <div className="space-y-3">
                                  {(inq.admin_notes || inq.adminNotes).map((note) => (
                                    <div key={note.id || `${note.by}-${note.at}`} className="rounded-lg bg-slate-50 p-3">
                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="text-sm font-medium">{note.by || "Admin"}</div>
                                        <div className="text-xs text-muted-foreground">{note.at ? new Date(note.at).toLocaleString() : "Unknown time"}</div>
                                      </div>
                                      <div className="text-sm text-foreground mt-2 whitespace-pre-line">{note.note || "No note content"}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 sm:hidden">
            {filteredItems.map((inq) => (
              <div key={inq.id} className="bg-white border border-gray-100 rounded-lg p-4" data-testid={`my-inq-mobile-${inq.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-sm break-words">{inq.id}</div>
                    <div className="text-xs text-muted-foreground mt-1">{inq.items?.length ?? 0} products · {formatDate(inq.created_at)}</div>
                  </div>
                  <div className="ml-2">
                    <Badge className={`${STATUS_COLOR[inq.status] || "bg-muted text-muted-foreground"} border-0`}>{String(inq.status || "unknown").replace(/_/g, " ")}</Badge>
                  </div>
                </div>

                <div className="mt-3 text-sm text-muted-foreground line-clamp-3">{inq.notes || "—"}</div>

                <div className="mt-3 flex items-center justify-between">
                  <button onClick={() => setExpandedId(expandedId === inq.id ? null : inq.id)} className="text-xs text-primary">
                    {expandedId === inq.id ? "Hide details" : "View details"}
                  </button>
                  <div className="text-xs text-muted-foreground">{formatDate(inq.updated_at)}</div>
                </div>

                {expandedId === inq.id && (
                  <div className="mt-3 space-y-3">
                    {Array.isArray(inq.items) && inq.items.length > 0 ? (
                      inq.items.map((item, index) => (
                        <div key={`${item.product_id}-${index}`} className="rounded-xl border border-gray-200 bg-white p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium">{item.name || item.catalogNumber || "Product"}</div>
                              <div className="text-xs text-muted-foreground mt-1">Catalog No: {item.catalogNumber || "—"}</div>
                            </div>
                            <div className="text-sm font-semibold">Qty: {item.quantity ?? item.qty ?? 1}</div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-muted-foreground">No items available for this inquiry.</div>
                    )}

                    {Array.isArray(inq.admin_notes && inq.admin_notes.length ? inq.admin_notes : inq.adminNotes) && (
                      <div className="rounded-xl border border-gray-200 bg-white p-3">
                        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Admin notes</div>
                        <div className="space-y-2">
                          {(inq.admin_notes || inq.adminNotes).map((note) => (
                            <div key={note.id || `${note.by}-${note.at}`} className="rounded-lg bg-slate-50 p-2">
                              <div className="flex items-center justify-between">
                                <div className="text-sm font-medium">{note.by || "Admin"}</div>
                                <div className="text-xs text-muted-foreground">{note.at ? new Date(note.at).toLocaleString() : "Unknown time"}</div>
                              </div>
                              <div className="text-sm text-foreground mt-2 whitespace-pre-line">{note.note || "No note content"}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (<div className="space-y-1.5"><Label className="text-xs font-medium">{label}</Label>{children}</div>);
